import { KeyboardEvent, Dispatch, SetStateAction } from "react";
import { v4 } from "uuid";
import { sendStatistics } from "../../../../services/dataColectorService";
import { fetchInitQuestions } from "../../../../services/messageService";
import { logCritical, logDebug, logError, logInfo, logRisk, logTrace, logWarning } from "../../../../utils/logger";
import { useAppContext } from "../../../../AppDataContext";
import * as ErrorTypes from "../../../../errorHandler/errorType";

const SLEEP_BEFORE_CALL = 6000;
const SLEEP_BEFORE_RETRY = 10;

export interface Message {
  id: number;
  type: "user" | "bot" | "bot initial-questions";
  text: string;
}

const expected_status_code: number[] = [200, 201, 403, 401, 500];
let proxy: string | undefined;
let activePromise: Promise<void> | null = null;
let globalAbortController: AbortController | null = null;
let stopTyping = false;
let terminateJob = false;
let stopSession = false;
let retryAttempted = 0;
let timer: NodeJS.Timeout | undefined = undefined;
let waitingTime: number | undefined;
if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
  waitingTime = undefined;
} else {
  waitingTime = 120000;
}

/* ---------------------------------------------------------------------
   Streams the full response text in CHUNK_SIZE groups and delivers
   each chunk via the updateFlashChunk callback. When finished,
   calls onComplete.
--------------------------------------------------------------------- */
export function streamCharacters(
  botResponse: string,
  updateFlashChunk: (chunk: string) => void,
  onComplete: () => void
): void {
  const CHUNK_SIZE = 35;
  const DELAY_MS = 7;
  let currentIndex = 0;

  const typeNextChunk = () => {
    if (currentIndex < botResponse.length) {
      const nextChunk = botResponse.substring(currentIndex, currentIndex + CHUNK_SIZE);
      currentIndex += CHUNK_SIZE;
      updateFlashChunk(nextChunk);
      // If the document is hidden, use 0 delay for immediate processing.
      const delay = document.hidden ? 0 : DELAY_MS;
      setTimeout(typeNextChunk, delay);
    } else {
      onComplete();
    }
  };

  typeNextChunk();
}

/* ---------------------------------------------------------------------
   Original typeCharacterAsync (fallback version)
   This version wraps each new chunk in a span with class "flash-chunk".
--------------------------------------------------------------------- */
export function typeCharacterAsync(
  setMessages: Dispatch<SetStateAction<Message[]>>,
  currentIndex: any,
  botResponse: string,
  message_num: { message_num: number } | undefined,
  lastMessageId: { lastMessageId: number } | undefined
): Promise<void> {
  const CHUNK_SIZE = 35;
  const DELAY_MS = 7;
  const promise = new Promise<void>((resolve) => {
    const typeNextChunk = () => {
      if (stopTyping) {
        resolve();
        if (timer) clearTimeout(timer);
        return;
      }
      if (currentIndex.currIndex < botResponse.length) {
        const nextChunk = botResponse.substring(
          currentIndex.currIndex,
          currentIndex.currIndex + CHUNK_SIZE
        );
        currentIndex.currIndex += CHUNK_SIZE;
        setMessages((prevState) => {
          const updatedMessages = [...prevState];
          updatedMessages[updatedMessages.length - 1].text += `<span class="flash-chunk">${nextChunk}</span>`;
          return updatedMessages;
        });
        setTimeout(typeNextChunk, DELAY_MS);
      } else {
        if (message_num) {
          message_num.message_num++;
        }
        if (lastMessageId) {
          lastMessageId.lastMessageId++;
        }
        resolve();
      }
    };
    typeNextChunk();
  });
  activePromise = promise;
  promise.finally(() => {
    if (activePromise === promise) {
      activePromise = null;
    }
  });
  return promise;
}
export const handleInput = async (
  e: KeyboardEvent<HTMLTextAreaElement>,
  userInput: string,
  setMessages: Dispatch<SetStateAction<Message[]>>,
  setLoad: Dispatch<SetStateAction<boolean>>,
  messages: Message[],
  load: boolean,
  currentIndex: number,
  setFetchData: Dispatch<SetStateAction<boolean>>,
  setStopStreaming: Dispatch<SetStateAction<boolean>>,
  setError1: Dispatch<SetStateAction<boolean>>,
  setError2: Dispatch<SetStateAction<boolean>>,
  setError3: Dispatch<SetStateAction<string>>,
  userId: string,
  userClass: string,
  client_id: string,
  client_token: string,
  article_index: string | undefined,
  follow_up_question: string | undefined,
  proxy: string | undefined,
  statistic: void | any,
  language: string | null,
  AppDataContext: ReturnType<typeof useAppContext>
) => {
  logInfo("handleInput called with userInput:", userInput);
  if (userInput.trim() === "" || load) {
    logTrace("Input is empty or load is true, returning early.");
    return;
  }
  let currentIndexx: number = 0;
  const indexParam = { currIndex: currentIndexx };
  if (e.key === "Enter") {
    logInfo("Enter key pressed.");
    await streamingLoop(
      userInput,
      setMessages,
      setLoad,
      messages,
      load,
      currentIndex,
      setFetchData,
      setStopStreaming,
      setError1,
      setError2,
      setError3,
      userId,
      userClass,
      client_id,
      client_token,
      article_index,
      follow_up_question,
      proxy,
      statistic,
      language,
      AppDataContext,
      indexParam
      // You can pass additional callbacks here if using streamCharacters.
    );
  }
  logTrace("handleInput execution completed.");
};

export const handleButtonClick = async (
  userInput: string,
  setMessages: Dispatch<SetStateAction<Message[]>>,
  setLoad: Dispatch<SetStateAction<boolean>>,
  messages: Message[],
  setFetchData: Dispatch<SetStateAction<boolean>>,
  setStopStreaming: Dispatch<SetStateAction<boolean>>,
  setError1: Dispatch<SetStateAction<boolean>>,
  setError2: Dispatch<SetStateAction<boolean>>,
  setError3: Dispatch<SetStateAction<string>>,
  userId: string,
  userClass : string,
  client_id: string,
  client_token: string,
  article_index: string | undefined,
  proxy: string | undefined,
  statistic: void | any,
  language: string | null,
  AppDataContext: ReturnType<typeof useAppContext>
) => {
  logInfo("handleButtonClick called with userInput:", userInput);
  if (userInput.trim() === "") {
    logTrace("Input is empty, returning early.");
    return;
  }
  let currentIndex = 0;
  const indexParam = { currIndex: currentIndex };
  await streamingLoop(
    userInput,
    setMessages,
    setLoad,
    messages,
    true,
    currentIndex,
    setFetchData,
    setStopStreaming,
    setError1,
    setError2,
    setError3,
    userId,
    userClass,
    client_id,
    client_token,
    article_index,
    undefined,
    proxy,
    statistic,
    language,
    AppDataContext,
    indexParam
    // Again, pass in updateFlashChunk callback if using streamCharacters.
  );
  logTrace("handleButtonClick execution completed.");
};

export async function cancelOngoingOperations(forceabort = false) {
  logTrace("cancelOngoingOperations called with forceabort:", forceabort);
  try {
    if (activePromise == null && !forceabort) {
      logTrace("No active promise and forceabort is false, returning early.");
      return;
    }
    if (globalAbortController) {
      logWarning("Aborting ongoing operations.");
      globalAbortController.abort();
      globalAbortController = null;
      stopTyping = true;
      terminateJob = true;
      logRisk("Ongoing operations aborted.");
    }
  } catch (error) {
    logError("An error occurred while canceling ongoing operations:", error);
  }
  logTrace("cancelOngoingOperations execution completed.");
}

export const stopFetchingHandler = async (
  setFetchData: Dispatch<SetStateAction<boolean>>,
  setStopStreaming: Dispatch<SetStateAction<boolean>>,
  setLoad: Dispatch<SetStateAction<boolean>>
) => {
  logCritical("stopFetchingHandler called.");
  stopTyping = true;
  stopSession = true;
  setFetchData(false);
  setStopStreaming(true);
  setLoad(false);
  if (globalAbortController) {
    logRisk("globalAbortController.abort();");
    globalAbortController.abort();
  }
};

export const streamingLoop = async (
  userInput: string,
  setMessages: Dispatch<SetStateAction<Message[]>>,
  setLoad: Dispatch<SetStateAction<boolean>>,
  messages: Message[],
  load: boolean,
  currentIndex: number,
  setFetchData: Dispatch<SetStateAction<boolean>>,
  setStopStreaming: Dispatch<SetStateAction<boolean>>,
  setError1: Dispatch<SetStateAction<boolean>>,
  setError2: Dispatch<SetStateAction<boolean>>,
  setError3: Dispatch<SetStateAction<string>>,
  userId: string,
  userClass: string,
  client_id: string,
  client_token: string,
  article_index: string | undefined,
  follow_up_question: string | undefined,
  proxy: string | undefined,
  statistic: void | any,
  language: string | null,
  AppDataContext: ReturnType<typeof useAppContext>,
  indexParam: any
) => {
  const id = Math.floor(messages.length / 2) + 1;
  const message_id = id;
  const userMessage: Message = { id: message_id, type: "user", text: userInput };
  const updatedMessages = [...messages, userMessage];
  setMessages(updatedMessages);
  sessionStorage.setItem("message_id", message_id.toString());
  setLoad(true);
  setFetchData(true);
  setError1(false);
  setError2(false);
  setError3("");
  let error2Flag = false;
  let error3Msg = "";
  try {
    globalAbortController = null;
    logTrace("Cancelling ongoing operations.");
    await cancelOngoingOperations();
    logTrace("User input:", userInput);
    if (!sessionStorage.getItem("session_ID")) {
      sessionStorage.setItem("session_ID", v4().substring(0, 8));
    }
    const is_game_mode = window.location.pathname.includes("/game");
    const offset_from_utc = -new Date().getTimezoneOffset();
    logTrace("offset_from_utc:", offset_from_utc);
    sessionStorage.setItem("message_id", message_id.toString());
    const requestData = {
      messages: [
        {
          id: id,
          message_id: message_id,
          content: updatedMessages[updatedMessages.length - 1],
          client_id: client_id,
          session_id: sessionStorage.getItem("session_ID"),
          user_id: userId,
          user_class: userClass,
          article_index: article_index,
          chat_mode: is_game_mode,
          language: language,
          offset_from_utc,
        },
      ],
    };
    logInfo(requestData.messages);
    globalAbortController = new AbortController();
    const { signal } = globalAbortController;
    if (proxy) {
      let fullMsg = "";
      let partialMessage = "";
      let resHandle: any = null;
      let lastMessageId: { lastMessageId: number } = { lastMessageId: 0 };
      let message_num = { message_num: 0 };
      const startTime = Date.now();
      let attempt = 0;
      const maxAttempts = 4;
      stopSession = false;
      terminateJob = false;
      stopTyping = false;
      while (attempt < maxAttempts) {
        try {
          if (stopTyping || terminateJob || stopSession) {
            clearTimeout(timer);
            setMessages((prev) => [
              ...prev,
              { id: message_id + 1, type: "bot", text: "" },
            ]);
            setLoad(false);
            setError1(false);
            setError2(false);
            setError3("");
            globalAbortController = null;
            terminateJob = false;
            stopTyping = false;
            stopSession = false;
            indexParam.currIndex = 0;
            // Fallback to original typeCharacterAsync if needed:
            await typeCharacterAsync(setMessages, indexParam, "", message_num, lastMessageId);
            return;
          }
          if (!resHandle) {
            logInfo("before - postWithRetries");
            resHandle = await postWithRetries(proxy, requestData, expected_status_code, client_token);
            logInfo("after - postWithRetries");
            resHandle = { status: resHandle.status, body: await resHandle.json() };
            let res_status = resHandle.status;
            if (res_status === 500) {
              throw new ErrorTypes.NoHandleError("No handle found for the request");
            }
            if ([403, 401].includes(res_status)) {
              const content = resHandle.body;
              logTrace("resStatus === 403 | 401 :", content);
              const stats: { [key: string]: number } = {};
              if (content) {
                setMessages((prev) => [
                  ...prev,
                  { id: message_id, type: "bot", text: "" },
                ]);
                const message = content?.message?.message;
                if (message && message.includes("[\u200B\u200B\u200B")) {
                  stats["PRESENTED_PRO_LINK"] = 1;
                }
                setMessages((prev) => [
                  ...prev,
                  { id: message_id, type: "bot", text: content?.message?.message },
                ]);
                if (content?.message?.button_url) {
                  logTrace("button_url:", content.message.button_url);
                }
              } else {
                error3Msg =
                  "You’ve been crushing it!\nYou’ve reached the chat limit for now.\nPlease come back later.";
              }
              if (res_status === 403) {
                stats["LIMIT_MESSAGE_PRESENTED"] = 1;
              } else if (res_status === 401) {
                stats["BAD_USER_TOKEN_ERROR"] = 1;
              }
              sendStatistics(proxy, client_id, userId, client_token, stats, true, message_id);
              setError3(error3Msg);
              setLoad(false);
              return;
            }
            lastMessageId.lastMessageId = resHandle.body.page;
            logInfo("before - Promise");
            if (globalAbortController && globalAbortController.signal.aborted) {
              return;
            }
            await new Promise((resolve) => setTimeout(resolve, SLEEP_BEFORE_CALL));
            logInfo("before - Promise");
          }
          setMessages((prev) => [
            ...prev,
            { id: message_id, type: "bot", text: "" },
          ]);
          if (globalAbortController && globalAbortController.signal.aborted) {
            return;
          }
          stopTyping = terminateJob;
          logInfo("before - conversation");
          let { response, _retryAttempted } = await conversation(
            proxy,
            client_token,
            requestData,
            globalAbortController,
            timer,
            waitingTime,
            client_id,
            userId,
            userClass,
            sessionStorage.getItem("session_ID"),
            message_id,
            resHandle.body,
            lastMessageId.lastMessageId,
            message_num.message_num,
            setStopStreaming,
            load
          );
          logInfo("after - conversation");
          if (terminateJob || stopSession) {
            clearTimeout(timer);
            setMessages((prev) => [
              ...prev,
              { id: message_id + 1, type: "bot", text: "" },
            ]);
            setLoad(false);
            setError1(false);
            setError2(false);
            setError3("");
            globalAbortController = null;
            terminateJob = false;
            stopTyping = false;
            stopSession = false;
            indexParam.currIndex = 0;
            // Fallback to original typeCharacterAsync if needed:
            await typeCharacterAsync(setMessages, indexParam, "", message_num, lastMessageId);
            break;
          }
          if (response.status === 204) {
            throw new ErrorTypes.NoAvailableChunksError("No available chunks found for user input");
          }
          retryAttempted = _retryAttempted;
          clearTimeout(timer);
          sendStatistics(proxy, client_id, userId, client_token, statistic, true, message_id);
          let error1Flag = false;
          logTrace(response, " - response");
          if (response.body && response.ok) {
            try {
              logTrace("before - pipeThrough - Response time:", Date.now() - startTime);
              const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
              logTrace("after - pipeThrough - Response time:", Date.now() - startTime);
              while (true && !error1Flag && !error2Flag && !stopSession) {
                logTrace("start - await reader.read()");
                const { value, done } = await reader.read();
                if (stopTyping || terminateJob || done) {
                  setLoad(false);
                  if (terminateJob) {
                    setError1(false);
                    setError2(false);
                    setError3("");
                    setMessages((prev) => [
                      ...prev,
                      { id: message_id, type: "bot", text: "" },
                    ]);
                    globalAbortController = null;
                    terminateJob = false;
                  }
                  stopTyping = false;
                  break;
                }
                if (stopTyping) {
                  stopSession = true;
                  return;
                }
                partialMessage += value;
                logTrace(partialMessage, " - partialMessage");
                let delimiterIndex;
                while ((delimiterIndex = partialMessage.indexOf("\n\n")) !== -1) {
                  const messageString = partialMessage.slice(0, delimiterIndex);
                  partialMessage = partialMessage.slice(delimiterIndex + 2);
                  const parts = messageString.split("\n");
                  const eventData = parts.find((part) => part.startsWith("data:"));
                  if (eventData) {
                    logTrace(eventData, " - eventData");
                    const jsonString = eventData.replace(/^data:\s*/, "");
                    logTrace(jsonString, " - jsonString");
                    let dataObject: any; // Explicitly type dataObject as any.
                    if (jsonString.includes("'") && jsonString.indexOf("'") < 5) {
                      if (jsonString.includes('"')) {
                        const firstQuoteIndex = jsonString.indexOf('"');
                        logTrace(firstQuoteIndex, " - firstQuoteIndex");
                        const secondQuoteIndex = jsonString.indexOf('"', firstQuoteIndex + 1);
                        logTrace(secondQuoteIndex, " - secondQuoteIndex");
                        const substringBetweenQuotes = jsonString.substring(firstQuoteIndex + 1, secondQuoteIndex);
                        logTrace(substringBetweenQuotes, " - substringBetweenQuotes");
                        const substringToRemove = `\"${substringBetweenQuotes}\"`;
                        logTrace(substringToRemove, " - substringToRemove");
                        const modifiedJsonString = jsonString.replace(substringToRemove, "''");
                        logTrace(modifiedJsonString, " - modifiedJsonString");
                        dataObject = JSON.parse(modifiedJsonString.replace(/'/g, '"'));
                        dataObject.formatted_response = substringBetweenQuotes;
                        logTrace(dataObject, " - dataObject");
                      } else {
                        dataObject = JSON.parse(jsonString.replace(/'/g, '"'));
                      }
                    } else {
                      dataObject = JSON.parse(jsonString);
                    }
                    logTrace(dataObject, " - dataObject");
                    if (dataObject.status_code === "<ERROR>") {
                      throw new ErrorTypes.LlmError("LLm failed the create response");
                    } else if (dataObject.status_code === "<ACTION>") {
                      const obj = dataObject.formatted_response;
                      logTrace(dataObject.formatted_response, " - Action - dataObject.formatted_response");
                      Object.entries(obj).forEach(([actionKey, actionValue]) => {
                        const actionValueTyped = actionValue as {
                          inputs: { [key: string]: string };
                          action: string;
                          action_tag: string;
                        };
                        if (!AppDataContext.actionDataContextProps) {
                          AppDataContext.actionDataContextProps = [];
                        }
                        AppDataContext.actionDataContextProps.push({
                          inputs: actionValueTyped.inputs,
                          actionKey: message_id.toString(),
                          action: actionValueTyped.action,
                          actionTag: actionValueTyped.action_tag,
                          className: actionValueTyped.action,
                        });

                        logDebug(AppDataContext.actionDataContextProps, " - AppDataContext.actionDataContextProps");
                      });
                    } else {
                      fullMsg += dataObject.formatted_response;
                      indexParam.currIndex = 0;
                      // Here we use streamCharacters to update the display.
                      await new Promise<void>((resolve) => {
                        streamCharacters(
                          dataObject.formatted_response,
                          (chunk: string) => {
                            // In your component you should pass the updateFlashChunk callback.
                            // Here we append the chunk directly to the message.
                            setMessages((prevState) => {
                              const updatedMessages = [...prevState];
                              updatedMessages[updatedMessages.length - 1].text += chunk;
                              return updatedMessages;
                            });
                          },
                          resolve
                        );
                      });
                    }
                  }
                }
              }
              logDebug(fullMsg, " - fullMsg");
              if (terminateJob) {
                setMessages([]);
                AppDataContext.actionDataContextProps = null;
                AppDataContext.ActionList = undefined;
                const startQuestionsText: any = await fetchInitQuestions(
                  proxy,
                  client_token,
                  client_id,
                  userId,
                  null,
                  language,
                  null,
                  null,
                  null
                );
                setMessages([startQuestionsText]);
                terminateJob = false;
                stopTyping = terminateJob;
                globalAbortController = null;
              }
              setLoad(false);
              indexParam.currIndex = 0;
            } catch (error) {
              if (error instanceof ErrorTypes.LlmError) {
                throw new ErrorTypes.LlmError("Error occurred in the middle of the stream message num is " + lastMessageId.lastMessageId);
              } else {
                throw new ErrorTypes.MidStreamError("Error occurred in the middle of the stream message num is " + lastMessageId.lastMessageId);
              }
            }
          } else {
            throw new ErrorTypes.NoStreamObjError("No stream object found in response");
          }
          break;
        } catch (error: any) {
          logTrace(`Error: in attempt ${attempt}`, error);
          switch (error.name) {
            case "NoHandleError":
              resHandle = null;
              lastMessageId.lastMessageId = 0;
              message_num.message_num = 0;
              logError("Caught NoHandleError:", error.message);
              if (attempt === maxAttempts - 1) {
                indexParam.currIndex = 0;
                error3Msg = "Uh-oh! We hit a technical snag and can't keep chatting";
                setMessages((prev) => [...prev, { id: message_id + 1, type: "bot", text: "" }]);
                await typeCharacterAsync(setMessages, indexParam, " " + error3Msg, message_num, lastMessageId);
                setError3(error3Msg);
                setLoad(false);
                currentIndex = 0;
              }
              break;
            case "NoStreamObjError":
              resHandle = null;
              lastMessageId.lastMessageId = 0;
              message_num.message_num = 0;
              logError("Caught NoStreamObjError:", error.message);
              await new Promise((resolve) => setTimeout(resolve, 2000));
              if (attempt === maxAttempts - 1) {
                indexParam.currIndex = 0;
                error3Msg = "Uh-oh!! We hit a technical snag and can't keep chatting";
                await typeCharacterAsync(setMessages, indexParam, " " + error3Msg, message_num, lastMessageId);
                setMessages((prev) => [...prev, { id: message_id + 1, type: "bot", text: "" }]);
                await typeCharacterAsync(setMessages, indexParam, " ", message_num, lastMessageId);
                setError3(error3Msg);
                setLoad(false);
                currentIndex = 0;
              }
              break;
            case "MidStreamError":
              logError("Caught MidStreamError:", error.message);
              if (attempt === maxAttempts - 1) {
                indexParam.currIndex = 0;
                error3Msg = "Uh-oh! We hit a technical snag and can't keep chatting";
                await typeCharacterAsync(setMessages, indexParam, " " + error3Msg, message_num, lastMessageId);
                setMessages((prev) => [...prev, { id: message_id + 1, type: "bot", text: "" }]);
                await typeCharacterAsync(setMessages, indexParam, " ", message_num, lastMessageId);
                setError3(error3Msg);
                setLoad(false);
                currentIndex = 0;
              }
              break;
            case "LlmError":
              resHandle = null;
              lastMessageId.lastMessageId = 0;
              message_num.message_num = 0;
              logError("Caught LlmError:", error.message);
              if (attempt === maxAttempts - 1) {
                setError1(true);
                logError("Error1 - '<ERROR.' -  occurred:");
              }
              break;
            case "AbortError":
              resHandle = null;
              clearTimeout(timer);
              setMessages((prev) => [...prev, { id: message_id + 1, type: "bot", text: "" }]);
              setLoad(false);
              setError1(false);
              setError2(false);
              setError3("");
              globalAbortController = null;
              terminateJob = false;
              stopTyping = false;
              stopSession = false;
              indexParam.currIndex = 0;
              await typeCharacterAsync(setMessages, indexParam, "", message_num, lastMessageId);
              return;
            default:
              resHandle = null;
              lastMessageId.lastMessageId = 0;
              message_num.message_num = 0;
              logError("Caught an unexpected error:", error);
              if (attempt === maxAttempts - 1) {
                indexParam.currIndex = 0;
                error3Msg = "Uh-oh! We hit a technical snag and can not keep chatting.";
                await typeCharacterAsync(setMessages, indexParam, " " + error3Msg, message_num, lastMessageId);
                setMessages((prev) => [...prev, { id: message_id + 1, type: "bot", text: "" }]);
                await typeCharacterAsync(setMessages, indexParam, " ", message_num, lastMessageId);
                setError3(error3Msg);
                setLoad(false);
                currentIndex = 0;
              }
              break;
          }
          attempt++;
          await new Promise((resolve) => setTimeout(resolve, SLEEP_BEFORE_RETRY));
        }
      }
    } else {
      error3Msg = "Uh-oh! We hit a technical snag and can't keep chatting";
      logTrace("Aborted because: " + signal + "- proxy: " + proxy);
      setError3(error3Msg);
    }
  } catch (error: any) {
    if (terminateJob && error.name === "AbortError") {
      setLoad(false);
      setError1(false);
      setError2(false);
      setError3("");
      setMessages([]);
      AppDataContext.actionDataContextProps = null;
      AppDataContext.ActionList = undefined;
      const startQuestionsText: any = await fetchInitQuestions(
        proxy,
        client_token,
        client_id,
        userId,
        null,
        language,
        null,
        null,
        null
      );
      setMessages([startQuestionsText]);
      terminateJob = false;
      stopTyping = terminateJob;
      globalAbortController = null;
      return;
    }
    if (error instanceof DOMException && error.name === "AbortError") {
      setLoad(false);
      if (!stopSession) {
        indexParam.currIndex = 0;
        await typeCharacterAsync(
          setMessages,
          indexParam,
          " Oops! It looks like the response is taking longer than usual.",
          undefined,
          undefined
        );
        error2Flag = true;
        setError2(error2Flag);
        logCritical(
          "error2 - handleInput - error instanceof DOMException && error.name === 'AbortError'"
        );
      }
    } else {
      indexParam.currIndex = 0;
      logCritical("handleInput - DOM-related error occurred:", error);
      await typeCharacterAsync(
        setMessages,
        indexParam,
        " Uh-oh! We hit a technical snag and can't keep chatting",
        undefined,
        undefined
      );
      setMessages((prev) => [...prev, { id: message_id + 1, type: "bot", text: "" }]);
      error3Msg = "Uh-oh! We hit a technical snag and can't keep chatting";
      setError3(error3Msg);
      setLoad(false);
      logCritical("Aborted because: - proxy: " + proxy + " reason: " + error);
      logError("Error:", error);
    }
  }
};

export const postWithRetries = async (
  proxy: string,
  payload: any,
  expected_http_status: number[],
  client_token: string,
  maxAttempts: number = 1
) => {
  let attempt = 0;
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: "Bearer " + client_token,
  };

  while (attempt < maxAttempts) {
    try {
      const response = await fetch(proxy + "/conversation", {
        method: "POST",
        headers: headers,
        body: JSON.stringify(payload),
      });
      if (expected_http_status.includes(response.status)) {
        return response;
      }
      attempt++;
      await new Promise((resolve) => setTimeout(resolve, 50));
    } catch (err) {
      attempt++;
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }
  throw new Error("Request failed after 3 attempts");
};

export const conversation = async (
  proxy: string,
  client_token: string,
  requestData: any,
  globalAbortController: AbortController | null,
  timer: NodeJS.Timeout | undefined,
  waitTime: number | undefined,
  clientId: any,
  userId: any,
  userClass: any,
  sessionId: any,
  messageId: any,
  res_handle: any,
  lastMessageId: any,
  message_num: any,
  setStopStreaming: Dispatch<SetStateAction<boolean>>,
  load: boolean
) => {
  const startTime = Date.now();
  let response: Response | null = null;
  let _retryAttempted = 0;
  let maxRetries = 1;
  let maxElapsedTime = 15000;
  const params = {
    clientId: clientId,
    userId: userId,
    userClass: userClass,
    sessionId: sessionId,
    messageId: messageId,
    _id: res_handle._id,
    part: res_handle.part,
    message_track_id: res_handle.message_track_id,
    page: lastMessageId,
    message_num: message_num,
  };
  while (_retryAttempted <= maxRetries) {
    try {
      let _waitingTime = waitTime;
      globalAbortController = new AbortController();
      const { signal } = globalAbortController;
      logTrace("Waiting time for response from server is: " + _waitingTime + "ms");
      if (_waitingTime !== undefined) {
        timer = setTimeout(() => {
          globalAbortController?.abort();
          logTrace("Request timed out.");
        }, _waitingTime);
      }
      logTrace("Waiting time for response after set from server is: " + _waitingTime + "ms");
      const streamUrl = `${proxy}/conversation?${new URLSearchParams(params).toString()}`;
      console.log(`Connecting to: ${streamUrl}`);
      response = await fetch(streamUrl, {
        method: "GET",
        headers: {
          Accept: "text/event-stream",
          Connection: "keep-alive",
          Authorization: "Bearer " + client_token,
        },
        signal: signal,
      });
      if (response.status !== 200 && response.status !== 204 && response.status !== undefined) {
        throw {
          response: {
            status: response.status,
            statusText: response.statusText,
          },
        };
      }
      break;
    } catch (error) {
      const elapsedTime = Date.now() - startTime;
      if (_retryAttempted < maxRetries && elapsedTime < maxElapsedTime) {
        _retryAttempted++;
        logError("Error occurred, retrying request:", error);
        clearTimeout(timer);
        let error_message = "Unknown error";
        if (error instanceof Error) {
          error_message = error.message;
        }
        try {
          sendStatistics(
            proxy,
            requestData.message.client_id,
            requestData.message.user_id,
            requestData.message.client_token,
            { RETRY_AFTER_CONVERSATION_ACK_FAILED: 1 },
            true,
            requestData.message.message_id
          );
        } catch {
          logError("Error sending error message to data collector");
        }
      } else {
        const error_message = "Request failed after retry:" + error;
        logError(error_message);
        throw new DOMException(error_message, "AbortError");
      }
    }
  }
  if (!response) {
    throw {
      response: {
        status: 500,
        statusText: "undefined error",
      },
    };
  }
  return { response, _retryAttempted };
};